import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserProfile, UserContextType, UserSupplement } from '@/types/user';
import { SecureStorage } from '@/utils/secureStorage';
import { userProfileService } from '@/services/userProfileService';
import { supabase } from '@/integrations/supabase/client';
import { supplementService } from '@/services/supplementService';
import { useDemoSupplements } from '@/hooks/useDemoSupplements';
import { z } from 'zod';

const UserSupplementSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string().optional(),
  dosage: z.string(),
  timing: z.enum(['morning', 'afternoon', 'evening', 'anytime']),
  suggestedTime: z.string(),
  icon: z.string(),
  active: z.boolean(),
  addedAt: z.string(),
});

const UserProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().optional(),
  createdAt: z.string(),
  supplements: z.array(UserSupplementSchema).default([]),
  preferences: z.object({
    timeFormat: z.enum(['12h', '24h']).default('12h'),
    notifications: z.boolean().default(true),
    defaultTimings: z.object({
      morning: z.string().default('08:00'),
      afternoon: z.string().default('13:00'),
      evening: z.string().default('19:00'),
    }).default({
      morning: '08:00',
      afternoon: '13:00',
      evening: '19:00',
    }),
  }).default({
    timeFormat: '12h',
    notifications: true,
    defaultTimings: {
      morning: '08:00',
      afternoon: '13:00',
      evening: '19:00',
    },
  }),
}).strict();

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Check if we're in demo mode
  const isDemoMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return import.meta.env.DEV || 
           urlParams.get('demo') === 'true' || 
           window.location.pathname.includes('/preview/');
  };
  
  // Demo supplements hook
  const demoSupplementsHook = useDemoSupplements();

  // Load user data on mount and handle auth changes
  useEffect(() => {
    let isMounted = true;
    
    // If in demo mode, set demo user immediately
    if (isDemoMode()) {
      const demoUser: UserProfile = {
        id: 'demo-user-123',
        name: 'Sarah Johnson',
        email: 'sarah.johnson@example.com',
        phone: '+1 (555) 123-4567',
        date_of_birth: '1990-05-15',
        avatar_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
        createdAt: new Date().toISOString(),
        supplements: demoSupplementsHook.supplements.map(sup => ({
          id: sup.id,
          name: sup.name,
          brand: sup.brand,
          dosage: sup.dosage,
          timing: sup.timing as 'morning' | 'afternoon' | 'evening' | 'anytime',
          suggestedTime: sup.suggestedTime,
          icon: sup.icon,
          active: true,
          addedAt: new Date().toISOString(),
        })),
        preferences: {
          timeFormat: '12h',
          notifications: true,
          defaultTimings: {
            morning: '08:00',
            afternoon: '13:00',
            evening: '19:00',
          }
        }
      };
      setCurrentUser(demoUser);
      setLoading(false);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;
        
        if (event === 'SIGNED_OUT' || !session?.user) {
          setCurrentUser(null);
          setLoading(false);
          return;
        }
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          await loadUserProfile(session.user.id);
        }
      }
    );

    // Initial load with timeout protection
    const loadUserProfile = async (userId: string) => {
      const timeout = 1000;
      const timeoutId = setTimeout(() => {
        // Create a minimal fallback profile
        const fallbackProfile: UserProfile = {
          id: userId,
          name: 'Sarah Johnson',
          email: 'sarah.johnson@example.com',
          createdAt: new Date().toISOString(),
          preferences: {
            timeFormat: '12h',
            notifications: true,
            defaultTimings: {
              morning: '08:00',
              afternoon: '13:00',
              evening: '19:00',
            }
          },
          supplements: []
        };
        setCurrentUser(fallbackProfile);
        setLoading(false);
      }, timeout);

      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();
        
        clearTimeout(timeoutId);
        
        if (error) {
          console.error('Error loading user profile:', error);
          // Try to create profile if it doesn't exist
          if (error.code === 'PGRST116') {
            await createMissingProfile(userId);
            return;
          }
          setCurrentUser(null);
        } else if (profile) {
          // Load user supplements from database
          let supplements: UserSupplement[] = [];
          if (profile.brand_id) {
            try {
              supplements = await supplementService.getUserSupplements(profile.id, profile.brand_id);
            } catch (error) {
              console.error('Error loading user supplements:', error);
              // Continue with empty supplements if loading fails
            }
          }

          const userProfile: UserProfile = {
            id: profile.id,
            name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email || 'User',
            email: profile.email,
            avatar_url: profile.avatar_url,
            createdAt: profile.created_at || new Date().toISOString(),
            supplements: supplements,
            preferences: (profile.preferences && typeof profile.preferences === 'object') 
              ? {
                  timeFormat: (profile.preferences as any)?.timeFormat || '12h',
                  notifications: (profile.preferences as any)?.notifications ?? true,
                  defaultTimings: (profile.preferences as any)?.defaultTimings || {
                    morning: '08:00',
                    afternoon: '13:00',
                    evening: '19:00',
                  },
                }
              : {
                  timeFormat: '12h' as const,
                  notifications: true,
                  defaultTimings: {
                    morning: '08:00',
                    afternoon: '13:00',
                    evening: '19:00',
                  },
                },
          };
          setCurrentUser(userProfile);
        } else {
          await createMissingProfile(userId);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('Error loading user profile:', error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    // Create missing profile helper
    const createMissingProfile = async (userId: string) => {
      try {
        // Get user info from auth
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user || !user.email) {
          console.error('Error getting user for profile creation:', userError || 'No user email found');
          return;
        }

        // Get default organization
        const { data: defaultOrg } = await supabase
          .from('organizations')
          .select('id')
          .eq('slug', 'default-org')
          .maybeSingle();

        const organizationId = defaultOrg?.id;

        // Extract name parts from email or metadata
        const emailUsername = user.email.split('@')[0];
        const firstName = user.user_metadata?.first_name || 
                         user.user_metadata?.name?.split(' ')[0] || 
                         emailUsername;
        const lastName = user.user_metadata?.last_name || 
                        user.user_metadata?.name?.split(' ').slice(1).join(' ') || 
                        '';

        // Create the profile
        const { data: newProfile, error: createError } = await supabase
          .from('user_profiles')
          .insert({
            id: userId,
            email: user.email,
            first_name: firstName,
            last_name: lastName,
            role: 'brand_admin',
            organization_id: organizationId,
            preferences: {
              timeFormat: '12h',
              notifications: true,
              defaultTimings: {
                morning: '08:00',
                afternoon: '13:00',
                evening: '19:00',
              },
            }
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating user profile:', createError);
          setCurrentUser(null);
          return;
        }
        
        // Set the new profile
        const userProfile: UserProfile = {
          id: newProfile.id,
          name: `${newProfile.first_name || ''} ${newProfile.last_name || ''}`.trim() || newProfile.email || 'User',
          email: newProfile.email,
          avatar_url: newProfile.avatar_url,
          createdAt: newProfile.created_at || new Date().toISOString(),
          supplements: [], // Start with empty supplements for new profiles
            preferences: (newProfile.preferences && typeof newProfile.preferences === 'object')
              ? {
                  timeFormat: (newProfile.preferences as any)?.timeFormat || '12h',
                  notifications: (newProfile.preferences as any)?.notifications ?? true,
                  defaultTimings: (newProfile.preferences as any)?.defaultTimings || {
                    morning: '08:00',
                    afternoon: '13:00',
                    evening: '19:00',
                  },
                } : {
              timeFormat: '12h' as const,
              notifications: true,
              defaultTimings: {
                morning: '08:00',
                afternoon: '13:00',
                evening: '19:00',
              },
            },
        };
        
        setCurrentUser(userProfile);
      } catch (error) {
        console.error('Error in createMissingProfile:', error);
        setCurrentUser(null);
      }
    };

    // Check for initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      
      if (session?.user) {
        loadUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const saveUser = async (user: UserProfile) => {
    try {
      // Check if user is authenticated
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (authUser) {
        // Use upsert to handle both insert and update cases
        const { error } = await supabase
          .from('user_profiles')
          .upsert({
            id: authUser.id,
            first_name: user.name.split(' ')[0] || '',
            last_name: user.name.split(' ').slice(1).join(' ') || '',
            email: user.email,
            avatar_url: user.avatar_url,
            preferences: user.preferences,
          }, {
            onConflict: 'id'
          });
          
        if (error) {
          console.error('Error saving user profile:', error);
          throw error;
        }
        
        setCurrentUser(user);
      } else {
        throw new Error('Authentication required to save user profile');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      throw error;
    }
  };

  const loadUser = (id: string): UserProfile | null => {
    // Load from database only
    return null;
  };

  const setUser = (user: UserProfile) => {
    setCurrentUser(user);
  };

  const addSupplement = async (supplement: Omit<UserSupplement, 'id' | 'addedAt'>) => {
    if (!currentUser) return;

    // Handle demo mode
    if (isDemoMode()) {
      demoSupplementsHook.addSupplement({
        name: supplement.name,
        brand: supplement.brand,
        dosage: supplement.dosage,
        timing: supplement.timing,
        suggestedTime: supplement.suggestedTime,
        icon: supplement.icon,
      });
      
      // Update current user with new supplements
      const updatedUser = {
        ...currentUser,
        supplements: [...currentUser.supplements, {
          ...supplement,
          id: `demo-${Date.now()}`,
          addedAt: new Date().toISOString(),
          active: true,
        }],
      };
      setCurrentUser(updatedUser);
      return;
    }

    try {
      // Get brand ID from current user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('brand_id')
        .eq('id', currentUser.id)
        .single();

      if (!profile?.brand_id) {
        console.error('No brand ID found for user');
        return;
      }

      const addedSupplement = await supplementService.addSupplement(currentUser.id, profile.brand_id, supplement);

      const updatedUser = {
        ...currentUser,
        supplements: [...currentUser.supplements, addedSupplement],
      };

      setCurrentUser(updatedUser);
    } catch (error) {
      console.error('Error adding supplement:', error);
    }
  };

  const removeSupplement = async (supplementId: string) => {
    if (!currentUser) return;

    // Handle demo mode
    if (isDemoMode()) {
      demoSupplementsHook.removeSupplement(supplementId);
      
      const updatedUser = {
        ...currentUser,
        supplements: currentUser.supplements.filter(s => s.id !== supplementId),
      };
      setCurrentUser(updatedUser);
      return;
    }

    try {
      await supplementService.removeSupplement(supplementId);

      const updatedUser = {
        ...currentUser,
        supplements: currentUser.supplements.filter(s => s.id !== supplementId),
      };

      setCurrentUser(updatedUser);
    } catch (error) {
      console.error('Error removing supplement:', error);
    }
  };

  const updateSupplement = async (supplementId: string, updates: Omit<UserSupplement, 'id' | 'addedAt'>) => {
    if (!currentUser) return;

    // Handle demo mode
    if (isDemoMode()) {
      const updatedUser = {
        ...currentUser,
        supplements: currentUser.supplements.map(s =>
          s.id === supplementId ? { ...s, ...updates } : s
        ),
      };
      setCurrentUser(updatedUser);
      return;
    }

    try {
      await supplementService.updateSupplement(supplementId, updates);

      const updatedUser = {
        ...currentUser,
        supplements: currentUser.supplements.map(s =>
          s.id === supplementId ? { ...s, ...updates } : s
        ),
      };

      setCurrentUser(updatedUser);
    } catch (error) {
      console.error('Error updating supplement:', error);
    }
  };

  return (
    <UserContext.Provider value={{
      currentUser,
      setUser,
      saveUser,
      loadUser,
      addSupplement,
      removeSupplement,
      updateSupplement,
      loading,
    }}>
      {children}
    </UserContext.Provider>
  );
};